import {
  Cancel,
  CardGiftcardSharp,
  CheckCircle,
  ContentCopy,
  DeleteSharp,
  MoreVertOutlined,
  SearchSharp,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import type { ChangeEvent, MouseEvent } from "react";
import { useCallback, useEffect, useMemo, useReducer } from "react";

import EditPointsDialog from "../../components/crm/EditPointsDialog";
import EmptyState from "../../components/crm/EmptyState";
import TableLoadingSkeleton, {
  type TableSkeletonColumn,
} from "../../components/crm/TableLoadingSkeleton";
import CustomConfirmDialog from "../../components/CustomConfirmDialog";
import CustomDialog from "../../components/CustomDialog";
import { useBreadcrumbs } from "../../contexts/BreadcrumbContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import {
  addCustomerPoints,
  deleteRedemption,
  getCustomer,
  getRedemptions,
  redeemCustomerPoints,
  updateRedemption,
} from "../../services/crm.service";
import { Customer, Redemption, RedemptionActionStatus } from "../../types/crm";
import { formatThaiPhone } from "../../utils/phone";
import { viewImage } from "../../services/upload.service";

type RedemptionListFilters = {
  search: string;
  month: string;
  year: string;
  status: RedemptionActionStatus | "all";
  page: number;
  page_size: number;
};

const MONTH_OPTIONS = [
  { label: "All Month", value: "all" },
  { label: "January", value: "01" },
  { label: "February", value: "02" },
  { label: "March", value: "03" },
  { label: "April", value: "04" },
  { label: "May", value: "05" },
  { label: "June", value: "06" },
  { label: "July", value: "07" },
  { label: "August", value: "08" },
  { label: "September", value: "09" },
  { label: "October", value: "10" },
  { label: "November", value: "11" },
  { label: "December", value: "12" },
];

const STATUS_OPTIONS: Array<{
  label: string;
  value: RedemptionListFilters["status"];
}> = [
  { label: "All State", value: "all" },
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
];

const getStatusChipConfig = (status: RedemptionActionStatus) => {
  if (status === "pending") {
    return {
      label: "Pending Approval",
      sx: { color: "#BB6902", bgcolor: "#FFFBC5" },
    };
  }

  if (status === "completed") {
    return {
      label: "Completed",
      sx: { color: "#2E7D32", bgcolor: "#E8F5E9" },
    };
  }

  return {
    label: "Reject",
    sx: { color: "#BE2217", bgcolor: "#FFE3E1" },
  };
};

const formatDateTime = (value?: string) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  if (!parsed.isValid()) return "-";
  return parsed.format("DD/MM/YYYY HH:mm");
};


const getPrivilegeStateLabel = (redemption: Redemption) => {
  const now = dayjs();
  const startDate = dayjs(redemption.privilege.start_date);
  const endDate = redemption.privilege.end_date
    ? dayjs(redemption.privilege.end_date)
    : null;

  if (endDate?.isValid() && endDate.isBefore(now, "day")) {
    return { label: "Expired", color: "#BE2217", bgColor: "#FFE3E1" };
  }

  if (startDate.isValid() && startDate.isAfter(now, "day")) {
    return { label: "Draft", color: "#6B7280", bgColor: "#EDEEF1" };
  }

  return { label: "Active", color: "#2D6830", bgColor: "#E3F5E3" };
};

const REDEMPTIONS_SKELETON_COLUMNS: TableSkeletonColumn[] = [
  { key: "checkbox", width: 74, isCheckbox: true },
  { key: "actions", width: 80 },
  { key: "no", width: 96 },
  { key: "ecpCode", width: 99 },
  { key: "lineUserId", width: 154 },
  { key: "customerName", width: 190 },
  { key: "phoneNumber", width: 182 },
  { key: "redemptionStatus", width: 177 },
  { key: "redeemName", width: 182 },
  { key: "detail", width: 236 },
  { key: "pointsUsed", width: 124 },
  { key: "redemptionDate", width: 170 },
  { key: "statusUpdateDate", width: 177 },
];

export default function CrmRedemptions() {
  const theme = useTheme();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { showSnackbar } = useSnackbar();
  const currentYear = dayjs().year();

  type State = {
    filters: RedemptionListFilters;
    redemptions: Redemption[];
    dataLoading: boolean;
    selectedIds: string[];
    isSubmitting: boolean;
    actionAnchorEl: HTMLElement | null;
    actionRow: Redemption | null;
    detailDialogOpen: boolean;
    approveDialogOpen: boolean;
    approveDetails: string;
    rejectDialogOpen: boolean;
    rejectDetails: string;
    deleteDialogOpen: boolean;
    editPointsDialogOpen: boolean;
    updateStatusValue: RedemptionActionStatus | "";
    updateStatusDetails: string;
    customerDetail: Customer | null;
    customerDetailLoading: boolean;
  };

  type Action =
    | { type: "SET_FILTERS"; payload: Partial<RedemptionListFilters> }
    | { type: "SET_REDEMPTIONS"; payload: Redemption[] }
    | { type: "SET_DATA_LOADING"; payload: boolean }
    | { type: "SET_SELECTED_IDS"; payload: string[] }
    | { type: "SET_IS_SUBMITTING"; payload: boolean }
    | { type: "SET_ACTION_ANCHOR_EL"; payload: HTMLElement | null }
    | { type: "SET_ACTION_ROW"; payload: Redemption | null }
    | { type: "SET_DETAIL_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_APPROVE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_APPROVE_DETAILS"; payload: string }
    | { type: "SET_REJECT_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_REJECT_DETAILS"; payload: string }
    | { type: "SET_DELETE_DIALOG_OPEN"; payload: boolean }
    | { type: "SET_EDIT_POINTS_DIALOG_OPEN"; payload: boolean }
    | { type: "RESET_ACTION_STATE" }
    | {
        type: "OPEN_ACTION_MENU";
        payload: { anchorEl: HTMLElement; row: Redemption };
      }
    | { type: "OPEN_APPROVE_DIALOG"; payload: string }
    | { type: "OPEN_REJECT_DIALOG"; payload: string }
    | { type: "SET_UPDATE_STATUS_VALUE"; payload: RedemptionActionStatus | "" }
    | { type: "SET_UPDATE_STATUS_DETAILS"; payload: string }
    | { type: "SET_CUSTOMER_DETAIL"; payload: Customer | null }
    | { type: "SET_CUSTOMER_DETAIL_LOADING"; payload: boolean };

  const initialState: State = {
    filters: {
      search: "",
      month: "all",
      year: String(currentYear),
      status: "all",
      page: 0,
      page_size: 10,
    },
    redemptions: [],
    dataLoading: false,
    selectedIds: [],
    isSubmitting: false,
    actionAnchorEl: null,
    actionRow: null,
    detailDialogOpen: false,
    approveDialogOpen: false,
    approveDetails: "",
    rejectDialogOpen: false,
    rejectDetails: "",
    deleteDialogOpen: false,
    editPointsDialogOpen: false,
    updateStatusValue: "",
    updateStatusDetails: "",
    customerDetail: null,
    customerDetailLoading: false,
  };

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case "SET_FILTERS":
        return {
          ...state,
          filters: { ...state.filters, ...action.payload },
          selectedIds: [],
        };
      case "SET_REDEMPTIONS":
        return { ...state, redemptions: action.payload };
      case "SET_DATA_LOADING":
        return { ...state, dataLoading: action.payload };
      case "SET_SELECTED_IDS":
        return { ...state, selectedIds: action.payload };
      case "SET_IS_SUBMITTING":
        return { ...state, isSubmitting: action.payload };
      case "SET_ACTION_ANCHOR_EL":
        return { ...state, actionAnchorEl: action.payload };
      case "SET_ACTION_ROW":
        return { ...state, actionRow: action.payload };
      case "SET_DETAIL_DIALOG_OPEN":
        return { ...state, detailDialogOpen: action.payload };
      case "SET_APPROVE_DIALOG_OPEN":
        return { ...state, approveDialogOpen: action.payload };
      case "SET_APPROVE_DETAILS":
        return { ...state, approveDetails: action.payload };
      case "SET_REJECT_DIALOG_OPEN":
        return { ...state, rejectDialogOpen: action.payload };
      case "SET_REJECT_DETAILS":
        return { ...state, rejectDetails: action.payload };
      case "SET_DELETE_DIALOG_OPEN":
        return { ...state, deleteDialogOpen: action.payload };
      case "SET_EDIT_POINTS_DIALOG_OPEN":
        return { ...state, editPointsDialogOpen: action.payload };
      case "SET_UPDATE_STATUS_VALUE":
        return { ...state, updateStatusValue: action.payload };
      case "SET_UPDATE_STATUS_DETAILS":
        return { ...state, updateStatusDetails: action.payload };
      case "SET_CUSTOMER_DETAIL":
        return { ...state, customerDetail: action.payload };
      case "SET_CUSTOMER_DETAIL_LOADING":
        return { ...state, customerDetailLoading: action.payload };
      case "RESET_ACTION_STATE":
        return { ...state, actionAnchorEl: null, actionRow: null, customerDetail: null };
      case "OPEN_ACTION_MENU":
        return {
          ...state,
          actionAnchorEl: action.payload.anchorEl,
          actionRow: action.payload.row,
        };
      case "OPEN_APPROVE_DIALOG":
        return {
          ...state,
          actionAnchorEl: null,
          approveDetails: action.payload,
          approveDialogOpen: true,
        };
      case "OPEN_REJECT_DIALOG":
        return {
          ...state,
          actionAnchorEl: null,
          rejectDetails: action.payload,
          rejectDialogOpen: true,
        };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(reducer, initialState);
  const {
    filters,
    redemptions,
    dataLoading,
    selectedIds,
    isSubmitting,
    actionAnchorEl,
    actionRow,
    detailDialogOpen,
    approveDialogOpen,
    approveDetails,
    rejectDialogOpen,
    rejectDetails,
    deleteDialogOpen,
    editPointsDialogOpen,
    updateStatusValue,
    updateStatusDetails,
    customerDetail,
    customerDetailLoading,
  } = state;

  useEffect(() => {
    setBreadcrumbs([{ label: "Redemption Tracking", to: "/crm/redemptions" }]);
  }, [setBreadcrumbs]);

  const yearOptions = useMemo(
    () => [
      { label: "All Year", value: "all" },
      ...Array.from({ length: 8 }, (_, index) => {
        const year = String(currentYear - index);
        return { label: year, value: year };
      }),
    ],
    [currentYear],
  );

  const fetchRedemptions = useCallback(async () => {
    dispatch({ type: "SET_DATA_LOADING", payload: true });
    try {
      const collected: Redemption[] = [];
      let currentPage = 0;
      let totalPages = 1;

      do {
        const response = await getRedemptions({
          page: currentPage,
          page_size: 100,
          sort: "created_at|-1",
          ...(filters.status !== "all" ? { status: filters.status } : {}),
        });

        collected.push(...(response?.data ?? []));
        totalPages = Math.max(response?.total_pages || 1, 1);
        currentPage += 1;
      } while (currentPage < totalPages && currentPage < 200);

      dispatch({ type: "SET_REDEMPTIONS", payload: collected });
    } catch (error) {
      console.error("Error loading redemptions:", error);
      dispatch({ type: "SET_REDEMPTIONS", payload: [] });
    } finally {
      dispatch({ type: "SET_DATA_LOADING", payload: false });
    }
  }, [filters.status]);

  useEffect(() => {
    fetchRedemptions();
  }, [fetchRedemptions]);

  const handleSearchChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { search: value, page: 0 } });
  }, []);

  const handleMonthChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { month: value, page: 0 } });
  }, []);

  const handleYearChange = useCallback((value: string) => {
    dispatch({ type: "SET_FILTERS", payload: { year: value, page: 0 } });
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    dispatch({
      type: "SET_FILTERS",
      payload: {
        status: value as RedemptionListFilters["status"],
        page: 0,
      },
    });
  }, []);

  const handlePageChange = useCallback((_: unknown, page: number) => {
    dispatch({ type: "SET_FILTERS", payload: { page } });
  }, []);

  const handlePageSizeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      dispatch({
        type: "SET_FILTERS",
        payload: {
          page_size: parseInt(event.target.value, 10),
          page: 0,
        },
      });
    },
    [],
  );

  const filteredRedemptions = useMemo(() => {
    const searchText = filters.search.trim().toLowerCase();

    return redemptions.filter((redemption) => {
      const redemptionDate = dayjs(redemption.redemption_time);

      if (filters.year !== "all") {
        const targetYear = Number(filters.year);
        if (!redemptionDate.isValid() || redemptionDate.year() !== targetYear) {
          return false;
        }
      }

      if (filters.month !== "all") {
        const targetMonth = Number(filters.month);
        if (
          !redemptionDate.isValid() ||
          redemptionDate.month() + 1 !== targetMonth
        ) {
          return false;
        }
      }

      if (!searchText) {
        return true;
      }

      const fullName =
        `${redemption.customer.name} ${redemption.customer.surname}`.trim();
      const searchable = [
        redemption.customer.ecp_code,
        redemption.customer.id,
        redemption.customer.phone,
        fullName,
        redemption.privilege.name,
        redemption.privilege.details,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(searchText);
    });
  }, [filters.month, filters.search, filters.year, redemptions]);

  const paginatedRedemptions = useMemo(() => {
    const start = filters.page * filters.page_size;
    return filteredRedemptions.slice(start, start + filters.page_size);
  }, [filteredRedemptions, filters.page, filters.page_size]);

  const allSelected =
    paginatedRedemptions.length > 0 &&
    paginatedRedemptions.every((row: Redemption) =>
      selectedIds.includes(row.id),
    );
  const isIndeterminate =
    selectedIds.length > 0 && !allSelected && paginatedRedemptions.length > 0;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      dispatch({
        type: "SET_SELECTED_IDS",
        payload: paginatedRedemptions.map((row: Redemption) => row.id),
      });
    } else {
      dispatch({ type: "SET_SELECTED_IDS", payload: [] });
    }
  };

  const handleSelectRow = (id: string) => {
    dispatch({
      type: "SET_SELECTED_IDS",
      payload: selectedIds.includes(id)
        ? selectedIds.filter((rowId: string) => rowId !== id)
        : [...selectedIds, id],
    });
  };

  const handleOpenActionMenu = (
    redemption: Redemption,
    event: MouseEvent<HTMLElement>,
  ) => {
    dispatch({
      type: "OPEN_ACTION_MENU",
      payload: { anchorEl: event.currentTarget, row: redemption },
    });
  };

  const handleCloseActionMenu = () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
  };

  const handleResetActionState = () => {
    dispatch({ type: "RESET_ACTION_STATE" });
  };

  const handleActionViewDetail = async () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
    if (actionRow) {
      dispatch({ type: "SET_UPDATE_STATUS_VALUE", payload: actionRow.status });
      // Fetch full customer details
      dispatch({ type: "SET_CUSTOMER_DETAIL_LOADING", payload: true });
      try {
        const detail = await getCustomer(actionRow.customer.id);
        dispatch({ type: "SET_CUSTOMER_DETAIL", payload: detail });
      } catch (error) {
        console.error("Error fetching customer detail:", error);
      } finally {
        dispatch({ type: "SET_CUSTOMER_DETAIL_LOADING", payload: false });
      }
    }
    dispatch({ type: "SET_UPDATE_STATUS_DETAILS", payload: "" });
    dispatch({ type: "SET_DETAIL_DIALOG_OPEN", payload: true });
  };

  const handleUpdateStatus = async () => {
    if (!actionRow?.id || !updateStatusValue || !updateStatusDetails.trim())
      return;
    await handleSubmitStatus(updateStatusValue as RedemptionActionStatus, updateStatusDetails.trim());
  };

  const handleSubmitStatus = async (
    status: RedemptionActionStatus,
    reason?: string,
  ) => {
    if (!actionRow?.id) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await updateRedemption(actionRow.id, { status, reason });
      dispatch({ type: "SET_DETAIL_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
      dispatch({ type: "SET_EDIT_POINTS_DIALOG_OPEN", payload: false });
      handleResetActionState();
      await fetchRedemptions();
      showSnackbar("Redemption status updated successfully", "success");
    } catch (error) {
      console.error("Error updating redemption status:", error);
      showSnackbar("Failed to update redemption status", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  const handleActionApprove = async () => {
    if (!actionRow?.id) return;
    const defaultText = `ยินดีด้วยคุณแลกคะแนนสำเร็จแล้ว โดยคุณได้ใช้คะแนนไป
${actionRow.points_used.toLocaleString()} คะแนน เพื่อแลกสิทธิพิเศษที่ชื่อว่า
”${actionRow.privilege.name || "-"}”`;
    dispatch({ type: "OPEN_APPROVE_DIALOG", payload: defaultText });
  };

  const handleActionReject = () => {
    if (!actionRow?.id) return;
    const defaultText = `ขออภัย การแลกสิทธิ์ “${actionRow.privilege.name || "-"}” ไม่สำเร็จ
เนื่องจากคำขอนี้ไม่ผ่านเงื่อนไขที่กำหนด`;
    dispatch({ type: "OPEN_REJECT_DIALOG", payload: defaultText });
  };

  const handleActionDelete = () => {
    dispatch({ type: "SET_ACTION_ANCHOR_EL", payload: null });
    dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: true });
  };

  const handleOpenEditPoints = () => {
    dispatch({ type: "SET_EDIT_POINTS_DIALOG_OPEN", payload: true });
  };

  const handleSubmitEditPoints = async (data: {
    mode: "increase" | "decrease";
    points: number;
    reason: string;
    expirationDate?: string;
    sendNotification: boolean;
  }) => {
    if (!actionRow?.customer?.id) return;

    try {
      if (data.mode === "increase") {
        await addCustomerPoints(actionRow.customer.id, {
          points: data.points,
          reason: data.reason,
          expires_on: data.expirationDate!,
          send_notification: data.sendNotification,
        });
      } else {
        await redeemCustomerPoints(actionRow.customer.id, {
          points: data.points,
          reason: data.reason,
          send_notification: data.sendNotification,
        });
      }

      await fetchRedemptions();
      showSnackbar("Customer points updated successfully", "success");
    } catch (error) {
      console.error("Error editing customer points:", error);
      showSnackbar("Failed to update customer points", "error");
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!actionRow?.id) return;
    dispatch({ type: "SET_IS_SUBMITTING", payload: true });
    try {
      await deleteRedemption(actionRow.id);
      dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
      handleResetActionState();
      await fetchRedemptions();
      showSnackbar("Redemption deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting redemption:", error);
      showSnackbar("Failed to delete redemption", "error");
    } finally {
      dispatch({ type: "SET_IS_SUBMITTING", payload: false });
    }
  };

  return (
    <Stack spacing={3}>
      <Paper sx={{ borderRadius: 0, overflow: "hidden" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ p: 2, flexWrap: "wrap", rowGap: 1.5 }}
        >
          <TextField
            size="small"
            placeholder="Search"
            value={filters.search}
            onChange={(event) => handleSearchChange(event.target.value)}
            sx={{ width: 300 }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchSharp sx={{ color: "#8E95A2", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <TextField
            select
            size="small"
            value={filters.month}
            onChange={(event) => handleMonthChange(String(event.target.value))}
            sx={{
              minWidth: 100,
              maxWidth: 180,
              "& .MuiSelect-select": {
                color:
                  filters.month === "all" ? "text.secondary" : "text.primary",
              },
            }}
          >
            {MONTH_OPTIONS.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={filters.year}
            onChange={(event) => handleYearChange(String(event.target.value))}
            sx={{ minWidth: 80, maxWidth: 180 }}
          >
            {yearOptions.map((year) => (
              <MenuItem key={year.value} value={year.value}>
                {year.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            value={filters.status}
            onChange={(event) => handleStatusChange(String(event.target.value))}
            sx={{
              minWidth: 100,
              maxWidth: 180,
              "& .MuiSelect-select": {
                color:
                  filters.status === "all" ? "text.secondary" : "text.primary",
              },
            }}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {dataLoading ? (
          <TableLoadingSkeleton columns={REDEMPTIONS_SKELETON_COLUMNS} rowCount={10} />
        ) : paginatedRedemptions.length > 0 ? (
          <>
            <TableContainer
              sx={{ borderTop: `1px solid ${theme.vars.palette.divider}` }}
            >
              <Table sx={{ minWidth: 1940 }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "& .MuiTableCell-root": {
                        height: 52,
                        borderBottom: `1px solid ${theme.vars.palette.divider}`,
                        color: "#25272C",
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        lineHeight: 1.4,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ width: 74, px: 2 }}>
                      <Checkbox
                        checked={allSelected}
                        indeterminate={isIndeterminate}
                        onChange={(event) =>
                          handleSelectAll(event.target.checked)
                        }
                        size="small"
                        sx={{ '&.Mui-checked': { color: '#1976D2' }, '&.MuiCheckbox-indeterminate': { color: '#1976D2' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 80, px: 2 }}>Actions</TableCell>
                    <TableCell sx={{ width: 96, px: 2 }}>No.</TableCell>
                    <TableCell sx={{ width: 99, px: 2 }}>ECP Code</TableCell>
                    <TableCell sx={{ width: 154, px: 2 }}>
                      LINE User ID
                    </TableCell>
                    <TableCell sx={{ width: 190, px: 2 }}>
                      Name Customer
                    </TableCell>
                    <TableCell sx={{ width: 182, px: 2 }}>
                      Phone Number
                    </TableCell>
                    <TableCell sx={{ width: 177, px: 2 }}>
                      Redemption Status
                    </TableCell>
                    <TableCell sx={{ width: 182, px: 2 }}>
                      Name Redeem
                    </TableCell>
                    <TableCell sx={{ width: 236, px: 2 }}>Detail</TableCell>
                    <TableCell sx={{ width: 124, px: 2 }}>
                      Points Used
                    </TableCell>
                    <TableCell sx={{ width: 170, px: 2 }}>
                      Redemption Date
                    </TableCell>
                    <TableCell sx={{ width: 177, px: 2 }}>
                      Status Update Date
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {paginatedRedemptions.map((redemption, index) => {
                    const statusChip = getStatusChipConfig(redemption.status);
                    return (
                      <TableRow
                        key={redemption.id}
                        selected={selectedIds.includes(redemption.id)}
                        sx={{
                          "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: "#F9FAFB",
                          },
                          "& .MuiTableCell-root": {
                            height: 79,
                            borderBottom: `1px solid ${theme.vars.palette.divider}`,
                            fontSize: 12,
                            color: "#25272C",
                            whiteSpace: "nowrap",
                          },
                        }}
                      >
                        <TableCell padding="checkbox" sx={{ width: 74, px: 2 }}>
                          <Checkbox
                            checked={selectedIds.includes(redemption.id)}
                            onChange={() => handleSelectRow(redemption.id)}
                            size="small"
                            sx={{ '&.Mui-checked': { color: '#1976D2' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 80, px: 2 }}>
                          <IconButton
                            size="small"
                            onClick={(event) =>
                              handleOpenActionMenu(redemption, event)
                            }
                          >
                            <MoreVertOutlined fontSize="small" />
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ width: 96, px: 2 }}>
                          {filters.page * filters.page_size + index + 1}
                        </TableCell>
                        <TableCell sx={{ width: 99, px: 2 }}>
                          {redemption.customer.ecp_code || "-"}
                        </TableCell>
                        <TableCell sx={{ width: 154, px: 2 }}>
                          {redemption.customer.id || "-"}
                        </TableCell>
                        <TableCell sx={{ width: 190, px: 2 }}>
                          {`${redemption.customer.name || ""} ${redemption.customer.surname || ""}`.trim() ||
                            "-"}
                        </TableCell>
                        <TableCell sx={{ width: 182, px: 2 }}>
                          {formatThaiPhone(redemption.customer.phone, "-")}
                        </TableCell>
                        <TableCell sx={{ width: 177, px: 2 }}>
                          <Chip
                            label={statusChip.label}
                            size="small"
                            sx={{
                              height: 24,
                              borderRadius: "4px",
                              fontSize: 12,
                              ...statusChip.sx,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 182, px: 2 }}>
                          <Box
                            sx={{
                              maxWidth: 150,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {redemption.privilege.name || "-"}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: 236, px: 2 }}>
                          <Box
                            sx={{
                              maxWidth: 204,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {redemption.privilege.details || "-"}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ width: 124, px: 2 }}>
                          {redemption.points_used.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ width: 170, px: 2 }}>
                          {formatDateTime(redemption.redemption_time)}
                        </TableCell>
                        <TableCell sx={{ width: 177, px: 2 }}>
                          {formatDateTime(redemption.updated_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={filteredRedemptions.length}
              rowsPerPage={filters.page_size}
              page={filters.page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
            />
          </>
        ) : (
          <EmptyState
            title="No redemptions found"
            description="No redemption data matches the selected filters."
          />
        )}
      </Paper>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleCloseActionMenu}
        slotProps={{
          paper: {
            sx: {
              minWidth: 200,
              borderRadius: "8px",
              boxShadow:
                "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
              overflow: "hidden",
            },
          },
          list: {
            sx: { p: 0 },
          },
        }}
      >
        <MenuItem
          onClick={handleActionViewDetail}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <Visibility sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="View Detail"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
        {actionRow?.status === "pending" && (
          <MenuItem
            onClick={handleActionApprove}
            disabled={isSubmitting}
            sx={{
              minHeight: 40,
              px: 3,
              py: 1,
              gap: 1,
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1.4,
              color: "#25272C",
              "&:hover": { bgcolor: "#EDEEF1" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
              <CheckCircle sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Approve"
              slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
            />
          </MenuItem>
        )}
        {actionRow?.status === "pending" && (
          <MenuItem
            onClick={handleActionReject}
            disabled={isSubmitting}
            sx={{
              minHeight: 40,
              px: 3,
              py: 1,
              gap: 1,
              fontSize: 16,
              fontWeight: 400,
              lineHeight: 1.4,
              color: "#25272C",
              "&:hover": { bgcolor: "#EDEEF1" },
            }}
          >
            <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
              <Cancel sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Reject"
              slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
            />
          </MenuItem>
        )}
        <MenuItem
          onClick={handleActionDelete}
          sx={{
            minHeight: 40,
            px: 3,
            py: 1,
            gap: 1,
            fontSize: 16,
            fontWeight: 400,
            lineHeight: 1.4,
            color: "#25272C",
            "&:hover": { bgcolor: "#EDEEF1" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 16, color: "#25272C" }}>
            <DeleteSharp sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            slotProps={{ primary: { sx: { fontSize: 14, lineHeight: 1.4 } } }}
          />
        </MenuItem>
      </Menu>

      <CustomDialog
        open={detailDialogOpen && !!actionRow}
        onClose={() => {
          dispatch({ type: "SET_DETAIL_DIALOG_OPEN", payload: false });
          handleResetActionState();
        }}
        title="Update Status Privilege Redemption"
        maxWidth="md"
        content={
          actionRow ? (
            <Stack sx={{ pt: 1, maxHeight: "72vh" }}>
              <Box sx={{ overflowY: "auto", pr: 0.5 }}>
                <Stack spacing={3}>
                  {/* Subtitle */}
                  <Typography
                    sx={{ fontSize: 16, color: "#8E95A2", lineHeight: 1.4 }}
                  >
                    Please review carefully before updating the status.
                  </Typography>

                  {/* Select Status */}
                  <Box>
                    <Typography sx={{ fontSize: 16, lineHeight: 1.4, py: 0.75 }}>
                      Select Status
                      <Box component="span" sx={{ color: "#BE2217" }}>
                        *
                      </Box>
                    </Typography>
                    <Select
                      fullWidth
                      value={updateStatusValue}
                      onChange={(event) =>
                        dispatch({
                          type: "SET_UPDATE_STATUS_VALUE",
                          payload: event.target.value as RedemptionActionStatus,
                        })
                      }
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected) return <Typography sx={{ color: "#8E95A2" }}>Select status</Typography>;
                        const config = getStatusChipConfig(selected as RedemptionActionStatus);
                        return (
                          <Chip
                            label={config.label}
                            size="small"
                            sx={{
                              height: 24,
                              borderRadius: "4px",
                              fontSize: 12,
                              ...config.sx,
                            }}
                          />
                        );
                      }}
                      sx={{
                        height: 56,
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#D8DBDF",
                        },
                      }}
                    >
                      <MenuItem value="pending">
                        <Chip
                          label="Pending Approval"
                          size="small"
                          sx={{
                            height: 24,
                            borderRadius: "4px",
                            fontSize: 12,
                            color: "#BB6902",
                            bgcolor: "#FFFBC5",
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="completed">
                        <Chip
                          label="Completed"
                          size="small"
                          sx={{
                            height: 24,
                            borderRadius: "4px",
                            fontSize: 12,
                            color: "#2E7D32",
                            bgcolor: "#E8F5E9",
                          }}
                        />
                      </MenuItem>
                      <MenuItem value="cancelled">
                        <Chip
                          label="Reject"
                          size="small"
                          sx={{
                            height: 24,
                            borderRadius: "4px",
                            fontSize: 12,
                            color: "#BE2217",
                            bgcolor: "#FFE3E1",
                          }}
                        />
                      </MenuItem>
                    </Select>
                  </Box>

                  {/* Details */}
                  <Box>
                    <Stack direction="row" spacing={0.25}>
                      <Typography sx={{ fontSize: 16, lineHeight: 1.4, py: 0.75 }}>
                        Details
                      </Typography>
                      <Typography sx={{ fontSize: 16, color: "#BE2217", lineHeight: 1.4, py: 0.75 }}>
                        *
                      </Typography>
                    </Stack>
                    <TextField
                      multiline
                      minRows={3}
                      fullWidth
                      value={updateStatusDetails}
                      onChange={(event) =>
                        dispatch({
                          type: "SET_UPDATE_STATUS_DETAILS",
                          payload: event.target.value,
                        })
                      }
                      placeholder="Enter Details"
                    />
                  </Box>

                  {/* Redemption Date */}
                  <Box>
                    <Typography
                      sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
                    >
                      Redemption Date
                    </Typography>
                    <Box sx={{ mt: 2, width: 340 }}>
                      <Typography
                        sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                      >
                        Date
                      </Typography>
                      <Typography sx={{ fontSize: 16, color: "#101828" }}>
                        {formatDateTime(actionRow.redemption_time)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Privilege Detail */}
                  <Stack spacing={2}>
                    <Typography
                      sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
                    >
                      Privilege Detail
                    </Typography>

                    <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                      <Box
                        sx={{
                          width: 156,
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid #D8DBDF",
                          backgroundColor: "#FFFFFF",
                          flexShrink: 0,
                        }}
                      >
                        {actionRow.privilege.banner_image ? (
                          <Box
                            component="img"
                            src={viewImage(actionRow.privilege.banner_image)}
                            alt=""
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                              e.currentTarget.style.display = "none";
                              e.currentTarget.nextElementSibling?.removeAttribute("style");
                            }}
                            sx={{
                              width: "100%",
                              height: 110,
                              objectFit: "cover",
                            }}
                          />
                        ) : null}
                        <Box
                          sx={{
                            width: "100%",
                            height: 110,
                            bgcolor: "#F3F4F6",
                            display: actionRow.privilege.banner_image ? "none" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography sx={{ fontSize: 12, color: "#8E95A2" }}>
                            No Image
                          </Typography>
                        </Box>
                        <Stack spacing={1} sx={{ p: 2 }}>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: 1.4,
                              height: 42,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {actionRow.privilege.name || "-"}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.25}
                            justifyContent="flex-end"
                            alignItems="baseline"
                          >
                            <Typography
                              sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                lineHeight: 1.5,
                              }}
                            >
                              {actionRow.points_used.toLocaleString()}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                lineHeight: 1.5,
                              }}
                            >
                              คะแนน
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>

                      <Stack spacing={2} sx={{ flex: 1 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Privileges ID
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {actionRow.id || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              State Privileges
                            </Typography>
                            <Chip
                              label={getPrivilegeStateLabel(actionRow).label}
                              size="small"
                              sx={{
                                height: 24,
                                borderRadius: "4px",
                                fontSize: 12,
                                color: getPrivilegeStateLabel(actionRow).color,
                                bgcolor:
                                  getPrivilegeStateLabel(actionRow).bgColor,
                              }}
                            />
                          </Box>
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Start Date
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {actionRow.privilege.start_date
                                ? dayjs(actionRow.privilege.start_date).format("DD/MM/YYYY")
                                : "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Expiration Date
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {actionRow.privilege.end_date
                                ? dayjs(actionRow.privilege.end_date).format("DD/MM/YYYY")
                                : "-"}
                            </Typography>
                          </Box>
                        </Stack>

                        <Box>
                          <Typography
                            sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                          >
                            Name Privilege
                          </Typography>
                          <Typography sx={{ fontSize: 16, color: "#101828" }}>
                            {actionRow.privilege.name || "-"}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                          >
                            Detail
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 16,
                              color: "#101828",
                              whiteSpace: "pre-wrap",
                              lineHeight: 1.4,
                            }}
                          >
                            {actionRow.privilege.details || "-"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </Stack>

                  {/* Detail Customer */}
                  <Stack spacing={2}>
                    <Typography
                      sx={{ fontSize: 20, fontWeight: 600, lineHeight: 1.4 }}
                    >
                      Detail Customer
                    </Typography>
                    <Box
                      sx={{
                        border: "1px solid #D8DBDF",
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{
                            background:
                              "linear-gradient(180deg, #333333 0%, #000000 100%)",
                            border: "1px solid rgba(255,255,255,0.16)",
                            borderRadius: 1,
                            p: 2,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <CardGiftcardSharp
                              sx={{ color: "#FFFFFF", fontSize: 32 }}
                            />
                            <Box>
                              <Typography
                                sx={{
                                  color: "#FFFFFF",
                                  fontSize: 16,
                                  fontWeight: 700,
                                  lineHeight: 1.5,
                                }}
                              >
                                {customerDetailLoading ? "..." : customerDetail?.points?.toLocaleString() ?? "-"}
                              </Typography>
                              <Typography
                                sx={{
                                  color: "#FFFFFF",
                                  fontSize: 14,
                                  lineHeight: 1.4,
                                }}
                              >
                                Valid until{" "}
                                {customerDetailLoading
                                  ? "..."
                                  : (() => {
                                      const nearest = customerDetail?.points_breakdown?.expiring_points
                                        ?.slice()
                                        .sort(
                                          (a, b) =>
                                            new Date(a.expires_on).getTime() - new Date(b.expires_on).getTime(),
                                        )[0];
                                      return nearest
                                        ? dayjs(nearest.expires_on).format("DD/MM/YYYY")
                                        : "-";
                                    })()}
                              </Typography>
                            </Box>
                          </Stack>
                          <Button
                            variant="contained"
                            onClick={handleOpenEditPoints}
                            sx={{
                              bgcolor: "#FFFFFF",
                              color: "#111111",
                              borderRadius: "96px",
                              px: 3,
                              height: 40,
                              boxShadow: "none",
                              fontSize: 16,
                              fontWeight: 700,
                              textTransform: "none",
                              "&:hover": {
                                bgcolor: "#F5F5F5",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Edit Points
                          </Button>
                        </Stack>
                      </Box>
                      <Stack spacing={2} sx={{ p: 2, pt: 0 }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              ECP CODE
                            </Typography>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Typography sx={{ fontSize: 16, color: "#101828" }}>
                                {actionRow.customer.ecp_code || "-"}
                              </Typography>
                              <ContentCopy
                                sx={{ fontSize: 16, color: "#8E95A2", cursor: "pointer" }}
                              />
                            </Stack>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Name Customer
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {`${actionRow.customer.name || ""} ${actionRow.customer.surname || ""}`.trim() ||
                                "-"}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Shop Name
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {customerDetailLoading ? "..." : customerDetail?.shop_name || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Email
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {customerDetailLoading ? "..." : customerDetail?.email || "-"}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Line User ID
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {actionRow.customer.id || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Phone Number
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {formatThaiPhone(actionRow.customer.phone, "-")}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Line Name
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {customerDetailLoading ? "..." : customerDetail?.line_name || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Created Date
                            </Typography>
                            <Typography sx={{ fontSize: 16, color: "#101828" }}>
                              {formatDateTime(actionRow.created_at)}
                            </Typography>
                          </Box>
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Line Verification
                            </Typography>
                            {customerDetailLoading ? (
                              <Typography sx={{ fontSize: 16, color: "#101828" }}>...</Typography>
                            ) : (
                              <Chip
                                size="small"
                                label={customerDetail?.line_name ? "Active" : "Inactive"}
                                sx={{
                                  bgcolor: customerDetail?.line_name ? "#E3F5E3" : "#EDEEF1",
                                  color: customerDetail?.line_name ? "#2D6830" : "#6B7280",
                                  fontSize: 12,
                                  fontWeight: 400,
                                  borderRadius: "4px",
                                  height: 24,
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }} />
                        </Stack>

                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          spacing={2}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{ fontSize: 14, color: "#8E95A2", mb: 0.5 }}
                            >
                              Customer Status
                            </Typography>
                            {customerDetailLoading ? (
                              <Typography sx={{ fontSize: 16, color: "#101828" }}>...</Typography>
                            ) : customerDetail?.status ? (
                              <>
                                <Chip
                                  size="small"
                                  label={
                                    customerDetail.status.charAt(0).toUpperCase() +
                                    customerDetail.status.slice(1).replace(/_/g, " ")
                                  }
                                  sx={{
                                    bgcolor:
                                      customerDetail.status === "active"
                                        ? "#E3F5E3"
                                        : customerDetail.status === "rejected" || customerDetail.status === "suspended"
                                          ? "#FDECEA"
                                          : "#EDEEF1",
                                    color:
                                      customerDetail.status === "active"
                                        ? "#2D6830"
                                        : customerDetail.status === "rejected" || customerDetail.status === "suspended"
                                          ? "#BE2217"
                                          : "#6B7280",
                                    fontSize: 12,
                                    fontWeight: 400,
                                    borderRadius: "4px",
                                    height: 24,
                                  }}
                                />
                                {(() => {
                                  const latestStatus = customerDetail.status_history
                                    ?.slice()
                                    .sort(
                                      (a, b) =>
                                        new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
                                    )[0];
                                  return latestStatus?.remark ? (
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: "#6B7280",
                                        mt: 0.5,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {latestStatus.remark}
                                    </Typography>
                                  ) : null;
                                })()}
                              </>
                            ) : (
                              <Typography sx={{ fontSize: 16, color: "#101828" }}>-</Typography>
                            )}
                          </Box>
                          <Box sx={{ flex: 1 }} />
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Box>
              <Box
                sx={{
                  position: "sticky",
                  bottom: 0,
                  borderTop: "1px solid #D8DBDF",
                  backgroundColor: "#FFFFFF",
                  px: 2,
                  py: 2,
                  mt: 2,
                }}
              >
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <Button
                    variant="outlined"
                    disabled={isSubmitting}
                    onClick={() => {
                      dispatch({
                        type: "SET_DETAIL_DIALOG_OPEN",
                        payload: false,
                      });
                      handleResetActionState();
                    }}
                    sx={{
                      borderRadius: "96px",
                      px: 3,
                      height: 40,
                      textTransform: "none",
                      borderColor: "#111111",
                      color: "#111111",
                    }}
                  >
                    Discard
                  </Button>
                  <Button
                    variant="contained"
                    disabled={
                      isSubmitting ||
                      !updateStatusValue ||
                      !updateStatusDetails.trim()
                    }
                    onClick={handleUpdateStatus}
                    sx={{
                      borderRadius: "96px",
                      px: 3,
                      height: 40,
                      textTransform: "none",
                      bgcolor: "#111111",
                      "&:hover": { bgcolor: "#000000" },
                    }}
                  >
                    Update Status
                  </Button>
                </Stack>
              </Box>
            </Stack>
          ) : undefined
        }
      />

      <EditPointsDialog
        open={editPointsDialogOpen && !!actionRow}
        onClose={() =>
          dispatch({ type: "SET_EDIT_POINTS_DIALOG_OPEN", payload: false })
        }
        onSubmit={handleSubmitEditPoints}
      />

      <CustomDialog
        open={approveDialogOpen && !!actionRow}
        onClose={() =>
          dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: false })
        }
        title="Approve Redemption"
        maxWidth="sm"
        content={
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 32 / 2, lineHeight: 1.4, mb: 1 }}>
                Details{" "}
                <Box component="span" sx={{ color: "#BE2217" }}>
                  *
                </Box>
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={approveDetails}
                onChange={(event) =>
                  dispatch({
                    type: "SET_APPROVE_DETAILS",
                    payload: event.target.value,
                  })
                }
                placeholder="Enter approval details"
                slotProps={{
                  input: {
                    sx: { py: 1.5 },
                  },
                }}
              />
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="outlined"
                disabled={isSubmitting}
                onClick={() =>
                  dispatch({ type: "SET_APPROVE_DIALOG_OPEN", payload: false })
                }
                sx={{
                  borderRadius: "96px",
                  px: 3,
                  height: 40,
                  textTransform: "none",
                  borderColor: "#111111",
                  color: "#111111",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={isSubmitting || !approveDetails.trim()}
                onClick={() =>
                  handleSubmitStatus("completed", approveDetails.trim())
                }
                sx={{
                  borderRadius: "96px",
                  px: 3,
                  height: 40,
                  textTransform: "none",
                  bgcolor: "#111111",
                  "&:hover": { bgcolor: "#000000" },
                }}
              >
                Approve
              </Button>
            </Stack>
          </Stack>
        }
      />

      <CustomDialog
        open={rejectDialogOpen && !!actionRow}
        onClose={() =>
          dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: false })
        }
        title="Reject Redemption"
        maxWidth="sm"
        content={
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Box>
              <Typography sx={{ fontSize: 16, lineHeight: 1.4, mb: 1 }}>
                Details{" "}
                <Box component="span" sx={{ color: "#BE2217" }}>
                  *
                </Box>
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                value={rejectDetails}
                onChange={(event) =>
                  dispatch({
                    type: "SET_REJECT_DETAILS",
                    payload: event.target.value,
                  })
                }
                placeholder="Enter reject details"
                slotProps={{
                  input: {
                    sx: { py: 1.5 },
                  },
                }}
              />
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={2}>
              <Button
                variant="outlined"
                disabled={isSubmitting}
                onClick={() =>
                  dispatch({ type: "SET_REJECT_DIALOG_OPEN", payload: false })
                }
                sx={{
                  borderRadius: "96px",
                  px: 3,
                  height: 40,
                  textTransform: "none",
                  borderColor: "#111111",
                  color: "#111111",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                disabled={isSubmitting || !rejectDetails.trim()}
                onClick={() =>
                  handleSubmitStatus("cancelled", rejectDetails.trim())
                }
                sx={{
                  borderRadius: "96px",
                  px: 3,
                  height: 40,
                  textTransform: "none",
                }}
              >
                Reject
              </Button>
            </Stack>
          </Stack>
        }
      />

      <CustomConfirmDialog
        open={deleteDialogOpen && !!actionRow}
        onClose={() => {
          dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
          handleResetActionState();
        }}
        title="Delete Redemption"
        content={
          <Typography sx={{ py: 1 }}>
            Are you sure you want to delete this redemption?
          </Typography>
        }
        action={
          <Stack direction="row" spacing={2} sx={{ px: 3, pb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                dispatch({ type: "SET_DELETE_DIALOG_OPEN", payload: false });
                handleResetActionState();
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              Delete
            </Button>
          </Stack>
        }
      />
    </Stack>
  );
}
